import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class TextHighlight extends Component {
  /** @type {HTMLSpanElement[]} */
  #chars = [];

  /** @type {number} */
  #highlightedCount = 0;

  /** @type {string} */
  #highlightColor = '';

  /** @type {number} */
  #vh = 0;

  /** @type {number} */
  #rafId = 0;

  /** @type {(() => void) | null} */
  #scrollHandler = null;

  /** @type {(() => void) | null} */
  #resizeHandler = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#highlightColor = this.dataset.highlightColor?.trim() || '';

    if (this.dataset.isRte !== undefined) this.#formatTables();

    if (!this.#highlightColor) return;

    if (prefersReducedMotion()) {
      this.#initReducedMotion();
      return;
    }

    this.#chars = this.#buildCharSpans();
    if (!this.#chars.length) return;

    this.#vh = window.innerHeight;

    this.#scrollHandler = () => {
      if (this.#rafId) return;
      this.#rafId = requestAnimationFrame(() => {
        this.#rafId = 0;
        this.#onScroll();
      });
    };

    this.#resizeHandler = () => {
      this.#vh = window.innerHeight;
    };

    window.addEventListener('scroll', this.#scrollHandler, { passive: true });
    window.addEventListener('resize', this.#resizeHandler, { passive: true });

    this.#onScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }

    if (this.#resizeHandler) {
      window.removeEventListener('resize', this.#resizeHandler);
      this.#resizeHandler = null;
    }

    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = 0;
    }

    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @returns {HTMLSpanElement[]} */
  #buildCharSpans() {
    const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT, null);
    const textNodes = [];
    let node;

    while ((node = walker.nextNode())) {
      if (node.textContent.trim()) textNodes.push(node);
    }

    const spans = [];

    for (const textNode of textNodes) {
      const text = textNode.textContent;
      const fragment = document.createDocumentFragment();

      for (let i = 0; i < text.length; i++) {
        const span = document.createElement('span');
        span.textContent = text[i];
        span.className = 'text-highlight__char';
        spans.push(span);
        fragment.appendChild(span);
      }

      textNode.parentNode.replaceChild(fragment, textNode);
    }

    return spans;
  }

  #formatTables() {
    for (const table of this.querySelectorAll('table')) {
      if (table.parentNode?.classList.contains('rte-table-wrapper')) continue;
      const wrapper = document.createElement('div');
      wrapper.className = 'rte-table-wrapper';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  #initReducedMotion() {
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.style.color = this.#highlightColor;
        this.#observer.disconnect();
        this.#observer = null;
      },
      { threshold: 0.1 }
    );
    this.#observer.observe(this);
  }

  #onScroll() {
    const { top } = this.getBoundingClientRect();
    const progress = Math.min(1, Math.max(0, (this.#vh * 0.9 - top) / (this.#vh * 0.8)));
    const targetCount = Math.round(progress * this.#chars.length);

    if (targetCount === this.#highlightedCount) return;

    const increasing = targetCount > this.#highlightedCount;
    const from = Math.min(this.#highlightedCount, targetCount);
    const to = Math.max(this.#highlightedCount, targetCount);
    const color = increasing ? this.#highlightColor : '';

    for (let i = from; i < to; i++) {
      this.#chars[i].style.color = color;
    }

    this.#highlightedCount = targetCount;
  }
}

if (!customElements.get('text-highlight')) {
  customElements.define('text-highlight', TextHighlight);
}
